// Нормализация разделов, которые экран продукта объявляет сам через ctx.onSections —
// приводит любую форму (сгруппированные секции или плоский список) к MenuNode["sections"].
import React from "react";
import { Ico } from "@kit-v5";
// icon в onSections продукта — либо строка-id словаря Ico (правило 3, резолв на клиенте),
// либо уже готовый узел (продукты m2s/пабликатор рисуют иконку сами) — принимаем оба вида.
export function resolveScreenIcon(icon) {
    if (React.isValidElement(icon))
        return icon;
    if (typeof icon === "string" && icon in Ico)
        return Ico[icon];
    return Ico.doc;
}
function isPlainObject(v) {
    return !!v && typeof v === "object" && !Array.isArray(v);
}
// Ветка любой глубины: пункт может нести children — тот же вид рекурсивно
// (форма из дизайна, .claude/design/up/up-screen.jsx:4937-4938). Некорректная
// форма (не массив, объект без id/label, циклическая ссылка) — пункт просто
// выпадает, а не роняет шелл: seen — гард от циклов, MAX_DEPTH — от глубины,
// которую циклом получить нельзя (Proxy/геттеры), но подстраховаться дёшево.
const MAX_SECTION_DEPTH = 8;
function toScreenItem(it, seen, depth) {
    if (!isPlainObject(it) || seen.has(it) || depth > MAX_SECTION_DEPTH)
        return null;
    if (typeof it.id !== "string" && typeof it.id !== "number")
        return null;
    if (typeof it.label !== "string")
        return null;
    const nextSeen = new Set(seen).add(it);
    const children = Array.isArray(it.children)
        ? it.children.map((c) => toScreenItem(c, nextSeen, depth + 1)).filter((c) => c !== null)
        : undefined;
    return {
        id: String(it.id), label: String(it.label),
        icon: resolveScreenIcon(it.icon),
        count: it.count,
        disabled: it.disabled,
        ...(children && children.length ? { children } : {}),
    };
}
// Экран продукта объявляет разделы в своей форме: либо уже сгруппированные секции
// ({title?, items:[...]}, как m2s/Настройки), либо плоский список пунктов (mp/publisher).
export function normalizeScreenSections(raw) {
    if (!Array.isArray(raw))
        return null;
    const objs = raw.filter(isPlainObject);
    const toItem = (it) => toScreenItem(it, new Set(), 0);
    const toItems = (items) => items.map(toItem).filter((i) => i !== null);
    if (objs.length > 0 && Array.isArray(objs[0].items)) {
        return objs.map((s) => ({
            title: s.title, items: toItems(s.items ?? []),
        }));
    }
    return [{ items: toItems(objs) }];
}
// Содержательное равенство двух наборов разделов (без учёта icon — React-узел, не значение):
// правило 1 контракта заменяет разделы паспорта разделами смонтированного экрана целиком, но
// если содержимое то же самое — подмена не должна дёргать панель видимым перестроением.
function menuItemsEqual(a = [], b = []) {
    if (a.length !== b.length)
        return false;
    return a.every((ai, i) => {
        const bi = b[i];
        return ai.id === bi.id && ai.label === bi.label && ai.count === bi.count && !!ai.disabled === !!bi.disabled
            && menuItemsEqual(ai.children, bi.children);
    });
}
export function sectionsEqual(a, b) {
    if (a === b)
        return true;
    if (!a || !b || a.length !== b.length)
        return false;
    return a.every((sa, i) => {
        const sb = b[i];
        return sa.title === sb.title && menuItemsEqual(sa.items, sb.items);
    });
}
// Правило меню продукта в панели: есть ли непустые разделы у уже загруженного узла.
export function menuHasSections(m) {
    return !!m?.sections?.some((s) => (s.items ?? []).length > 0);
}
